import { GetServerSideProps } from 'next'
import { plainToInstance } from 'class-transformer'

import { ReadingPageProps } from '@features/story/pages/ChapterPage/interface'
import { ChapterPage } from '@features/story/pages/ChapterPage'
import { AppLayout } from '@components/layouts/AppLayout'
import { gqlApiInstance } from '@client/init'
import { BOOK_CHAPTER_META_DATA } from '@client/collections/Chapter/schemas/bookChapterMetaData'
import { BookChapterMetaDataType } from '@models/chapter/BookChapterMetaData'
import { getErrorMessage } from '@lib/utils'
import { ErrorMessage } from '@components/ErrorMessage'
import { isMaintenanceHandler } from '@lib/maintenanceHandler'
import { AppLinkMeta } from '@components/AppLinkMeta'

function ReadingPage(props: ReadingPageProps) {
  const { chapterId, bookId, metaData, errorMessage } = props

  if (errorMessage) {
    return (
      <AppLayout>
        <ErrorMessage message={errorMessage} redirectUrl='/' />
      </AppLayout>
    )
  }

  const tagsList = metaData.book.tags.map(item => item.name)

  return (
    <AppLayout
      seoDescription={`${metaData.book.title} ${metaData.chapterName} ${metaData.book.cover}`}
      appendKeyword={`${metaData.book.title} ${metaData.chapterName} ${tagsList} ${metaData.book.category.name}`}
    >
      <AppLinkMeta path={`story/${bookId}/chapter/${chapterId}`} />
      <ChapterPage chapterId={chapterId} bookId={bookId} metaData={metaData} />
    </AppLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const bookId = Number(params?.id)
  const chapterId = Number(params?.chapterId)

  try {
    const isMaintenance = await isMaintenanceHandler()
    if (isMaintenance) {
      return {
        redirect: {
          destination: '/maintenance',
          permanent: false,
        },
        props: {
          isMaintenance: !!isMaintenance,
        },
      }
    }

    const res = await gqlApiInstance.request(BOOK_CHAPTER_META_DATA, {
      id: chapterId,
    })

    const metaData = plainToInstance(
      BookChapterMetaDataType,
      res.bookChapterForShare
    )

    return {
      props: {
        key: `chapter-page-${chapterId}`,
        bookId,
        chapterId,
        metaData: JSON.parse(JSON.stringify(metaData)),
        isMaintenance: false,
      },
    }
  } catch (error: any) {
    if (error.response?.status === 503) {
      return {
        redirect: {
          destination: '/maintenance',
          permanent: false,
        },
        props: {
          isMaintenance: false,
        },
      }
    }

    return {
      props: {
        key: `chapter-page-${chapterId}`,
        bookId,
        chapterId,
        metaData: {},
        errorMessage: getErrorMessage(error),
        isMaintenance: false,
      },
    }
  }
}

export default ReadingPage
